import { useState } from 'react';
import Input from '../../../Abstracts/Components/Forms/Input';
import styled from 'styled-components';
import { EftposDepositAccount } from 'shared-components';
import { Colors } from '../../../Abstracts/Style/Colors';
import LoadingOverlay from '../../../Abstracts/Components/LoadingOverlay';

export type EditableEftposDepositAccount = Omit<
	EftposDepositAccount,
	'Created' | 'Modified' | 'EftposDepositAccountHistory'
>;

interface EditDepositAccountFormProps {
	depositAccount: EditableEftposDepositAccount;
	setDepositAccount: React.Dispatch<React.SetStateAction<EditableEftposDepositAccount | undefined>>;
	onSave: (menu: EditableEftposDepositAccount) => Promise<void>;
	saveButtonText: string;
}

export const formatBsb = (bsb: string) => {
	// remove any non-numeric characters
	const val = ('' + bsb).replace(/[^0-9]/g, '');
	// put a dash in the middle
	if (val.length === 6) {
		const newVal = val.slice(0, 3) + '-' + val.slice(3);
		return newVal;
	}
	return val;
};
const isBsbValid = (bsb: string | null) => {
	if (bsb === null) return false;
	return ('' + bsb).match(/^[0-9]{3}-[0-9]{3}$/);
};
const isAccountNumberValid = (accountNumber: string | null) => {
	if (accountNumber === null) return false;
	const val = '' + accountNumber;
	return val.length > 5 && val.length < 11 && !isNaN(parseInt(val));
};

export default function EditDepositAccountForm(props: EditDepositAccountFormProps) {
	const { depositAccount, setDepositAccount, onSave, saveButtonText } = props;
	const [loading, setLoading] = useState(false);

	const [validateActive, setValidateActive] = useState(false);

	const isValid =
		!validateActive ||
		(depositAccount.EftposDepositAccountName &&
			depositAccount.EftposDepositAccountName.length > 0 &&
			isBsbValid(depositAccount.EftposDepositAccountBsb) &&
			isAccountNumberValid(depositAccount.EftposDepositAccountAccount));

	return (
		<Container>
			{loading && <LoadingOverlay text="Saving account details" />}
			<div className="input-container">
				<Input
					className="account-name"
					label="Account name"
					placeholder="Account name"
					value={depositAccount.EftposDepositAccountName ?? ''}
					onChange={(val) => setDepositAccount({ ...depositAccount, EftposDepositAccountName: val })}
				/>
				{validateActive && (depositAccount.EftposDepositAccountName ?? '').length === 0 && (
					<div className="validation-container">An account name is required</div>
				)}
			</div>
			<div className="input-container">
				<Input
					className="account-bsb"
					label="BSB"
					placeholder="BSB"
					value={depositAccount.EftposDepositAccountBsb ?? ''}
					onBlur={(e) =>
						setDepositAccount({ ...depositAccount, EftposDepositAccountBsb: formatBsb(e.target.value) })
					}
					onChange={(val) => setDepositAccount({ ...depositAccount, EftposDepositAccountBsb: val })}
				/>
				{validateActive && !isBsbValid(depositAccount.EftposDepositAccountBsb) && (
					<div className="validation-container">Invalid BSB number</div>
				)}
			</div>
			<div className="input-container">
				<Input
					className="account-number"
					label="Account number"
					placeholder="Account number"
					value={depositAccount.EftposDepositAccountAccount ?? ''}
					onBlur={(e) => {
						// remove any non-numeric characters
						const val = e.target.value.replace(/[^0-9]/g, '');
						setDepositAccount({ ...depositAccount, EftposDepositAccountAccount: val });
					}}
					onChange={(val) => setDepositAccount({ ...depositAccount, EftposDepositAccountAccount: val })}
				/>
				{validateActive && !isAccountNumberValid(depositAccount.EftposDepositAccountAccount) && (
					<div className="validation-container">Invalid account number</div>
				)}
			</div>
			<button
				onClick={async () => {
					if (
						(depositAccount.EftposDepositAccountName ?? '').length === 0 ||
						!isBsbValid(depositAccount.EftposDepositAccountBsb) ||
						!isAccountNumberValid(depositAccount.EftposDepositAccountAccount)
					) {
						setValidateActive(true);
						return;
					}
					setLoading(true);
					try {
						await onSave({
							...depositAccount,
							EftposDepositAccountBsb: (depositAccount.EftposDepositAccountBsb ?? '').replace(/-/g, ''),
						});
					} catch (e) {
						setLoading(false);
						console.log(e);
					}
				}}
				className={`${!isValid ? 'disabled' : ''} btn btn-block btn-primary create-btn btn-lg`}
				disabled={!isValid}
			>
				{saveButtonText}
			</button>
		</Container>
	);
}

const Container = styled.div`
	display: flex;
	flex-direction: column;

	.create-btn {
		margin-block: 50px;
	}

	.form-group {
		margin: 0;
	}

	.input-container {
		width: 200px;
		.validation-container {
			color: ${Colors.red};
			font-size: 12px;
		}
		margin-bottom: 15px;
	}

	@media (max-width: 768px) {
		.input-container {
			width: 100%;
		}
	}
`;
